// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';
import { getDictionaryOptionsApiAdminDictionariesTypeOptionsGet } from './generated/dictionary';

const dictionaryOptionsCache = new Map<
  string,
  API.DictionaryOptionResponse[]
>();
const dictionaryOptionsPromises = new Map<
  string,
  Promise<API.DictionaryOptionResponse[]>
>();
const tagLexiconCache = new Map<
  string,
  API.DictionaryTagLexiconOptionResponse[]
>();
const tagLexiconPromises = new Map<
  string,
  Promise<API.DictionaryTagLexiconOptionResponse[]>
>();

function getDictionaryOptionsCacheKey(type: string, isActive: boolean): string {
  return `${type}_${isActive}`;
}

function getTagLexiconCacheKey(
  category: string | undefined,
  isActive: boolean | undefined,
): string {
  return `${category ?? 'benefit_guarantee'}_${String(isActive)}`;
}

export function clearDictionaryOptionsCache(type?: string): void {
  if (type) {
    for (const key of dictionaryOptionsCache.keys()) {
      if (key.startsWith(`${type}_`)) dictionaryOptionsCache.delete(key);
    }
    for (const key of dictionaryOptionsPromises.keys()) {
      if (key.startsWith(`${type}_`)) dictionaryOptionsPromises.delete(key);
    }
    return;
  }
  dictionaryOptionsCache.clear();
  dictionaryOptionsPromises.clear();
}

export function clearTagLexiconCache(): void {
  tagLexiconCache.clear();
  tagLexiconPromises.clear();
}

function clearDictionaryRelatedCaches(type?: string): void {
  clearDictionaryOptionsCache(type);
  clearTagLexiconCache();
}

/**
 * 创建字典项
 * POST /api/admin/dictionaries
 */
export async function createDictionary(
  body: API.DictionaryCreateRequest,
  options?: { [key: string]: any },
) {
  const res = await request<API.BaseResponse<API.DictionaryResponse>>(
    '/api/admin/dictionaries',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
  if (res.code === 0) clearDictionaryRelatedCaches(body.type);
  return res;
}

/**
 * 字典项列表（分页）
 * GET /api/admin/dictionaries
 */
export async function listDictionaries(
  params?: {
    current?: number;
    pageSize?: number;
    type?: string | null;
    parent_id?: string | null;
    is_active?: boolean | null;
    keyword?: string | null;
    sorter?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.PaginatedResponse<API.DictionaryResponse>>(
    '/api/admin/dictionaries',
    {
      method: 'GET',
      params: {
        ...params,
      },
      ...(options || {}),
    },
  );
}

/**
 * 字典项树形结构列表
 * GET /api/admin/dictionaries/tree
 * 返回分页格式的树形结构，data.list 中包含根节点列表
 */
export async function listDictionariesTree(
  params?: {
    type?: string | null;
    is_active?: boolean | null;
  },
  options?: { [key: string]: any },
) {
  return request<API.PaginatedResponseDictionaryTreeResponse_>(
    '/api/admin/dictionaries/tree',
    {
      method: 'GET',
      params: {
        ...params,
      },
      ...(options || {}),
    },
  );
}

/** 字典项下拉选项，带内存缓存，适合表单 select 使用 */
export async function getDictionaryOptionsCached(
  type: string,
  isActive: boolean = true,
  options?: { [key: string]: any },
): Promise<API.DictionaryOptionResponse[]> {
  const cacheKey = getDictionaryOptionsCacheKey(type, isActive);
  const cached = dictionaryOptionsCache.get(cacheKey);
  if (cached) return cached;

  const existing = dictionaryOptionsPromises.get(cacheKey);
  if (existing) return existing;

  const promise = (async (): Promise<API.DictionaryOptionResponse[]> => {
    const res = await getDictionaryOptionsApiAdminDictionariesTypeOptionsGet(
      { type, is_active: isActive },
      options,
    );
    const data = res.code === 0 && res.data ? res.data : [];
    dictionaryOptionsCache.set(cacheKey, data);
    return data;
  })();

  dictionaryOptionsPromises.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    dictionaryOptionsPromises.delete(cacheKey);
  }
}

/**
 * 岗位标签树（含岗位数）
 * GET /api/admin/dictionaries/job_position/tree-with-count
 * 返回树形结构，每个节点带 jd_count；兼容树结构，可直接用于展示。
 */
export async function listJobPositionTreeWithCount(
  params?: {
    is_active?: boolean | null;
    published_only?: boolean;
  },
  options?: { [key: string]: any },
) {
  return request<API.PaginatedResponseDictionaryTreeWithCountResponse_>(
    '/api/admin/dictionaries/job_position/tree-with-count',
    {
      method: 'GET',
      params: {
        ...params,
      },
      ...(options || {}),
    },
  );
}


/**
 * 获取字典项详情
 * GET /api/admin/dictionaries/{dictionary_id}
 */
export async function getDictionary(
  dictionaryId: string,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponse<API.DictionaryResponse>>(
    `/api/admin/dictionaries/${dictionaryId}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/**
 * 更新字典项
 * PUT /api/admin/dictionaries/{dictionary_id}
 */
export async function updateDictionary(
  dictionaryId: string,
  body: API.DictionaryUpdateRequest,
  options?: { [key: string]: any },
) {
  const res = await request<API.BaseResponse<API.DictionaryResponse>>(
    `/api/admin/dictionaries/${dictionaryId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
  if (res.code === 0) clearDictionaryRelatedCaches();
  return res;
}

/**
 * 删除字典项（逻辑删除）
 * DELETE /api/admin/dictionaries/{dictionary_id}
 */
export async function deleteDictionary(
  dictionaryId: string,
  options?: { [key: string]: any },
) {
  const res = await request<API.BaseResponse<Record<string, string>>>(
    `/api/admin/dictionaries/${dictionaryId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  if (res.code === 0) clearDictionaryRelatedCaches();
  return res;
}

/**
 * 预览字典代码（用于前端确认）
 * POST /api/admin/dictionaries/preview-code
 */
export async function previewDictionaryCode(
  body: API.DictionaryCodePreviewRequest,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponse<API.DictionaryCodePreviewResponse>>(
    '/api/admin/dictionaries/preview-code',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
}

/**
 * 预览字典显示文本（根据 value 自动生成 display_text）
 * POST /api/admin/dictionaries/preview-display-text
 */
export async function previewDisplayText(
  body: API.DictionaryDisplayTextPreviewRequest,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponse<API.DictionaryDisplayTextPreviewResponse>>(
    '/api/admin/dictionaries/preview-display-text',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
}

/**
 * 标签词库列表
 * GET /api/admin/dictionaries/tag-lexicons
 * - category 默认 benefit_guarantee（岗位福利下拉）；传 all 返回全部分类（运营后台列表）。
 * - 不传 is_active 时返回该范围下全部未删除项（含已停用）；传 true/false 则筛选启用状态。
 */
export async function listTagLexicons(
  params?: {
    category?: string;
    is_active?: boolean;
  },
  options?: { [key: string]: any },
) {
  const query: Record<string, string | boolean> = {
    category: params?.category ?? 'benefit_guarantee',
  };
  if (params?.is_active !== undefined) {
    query.is_active = params.is_active;
  }
  return request<API.BaseResponse<API.DictionaryTagLexiconOptionResponse[]>>(
    '/api/admin/dictionaries/tag-lexicons',
    {
      method: 'GET',
      params: query,
      ...(options || {}),
    },
  );
}

/** 标签词库选项，带内存缓存，适合岗位表单等只读下拉使用 */
export async function listTagLexiconsCached(
  params?: {
    category?: string;
    is_active?: boolean;
  },
  options?: { [key: string]: any },
): Promise<API.DictionaryTagLexiconOptionResponse[]> {
  const cacheKey = getTagLexiconCacheKey(params?.category, params?.is_active);
  const cached = tagLexiconCache.get(cacheKey);
  if (cached) return cached;

  const existing = tagLexiconPromises.get(cacheKey);
  if (existing) return existing;

  const promise = (async (): Promise<
    API.DictionaryTagLexiconOptionResponse[]
  > => {
    const res = await listTagLexicons(params, options);
    const data = res.code === 0 && res.data ? res.data : [];
    tagLexiconCache.set(cacheKey, data);
    return data;
  })();

  tagLexiconPromises.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    tagLexiconPromises.delete(cacheKey);
  }
}

/** POST /api/admin/dictionaries/tag-lexicons */
export async function createTagLexicon(
  body: API.DictionaryTagLexiconCreateRequest,
  options?: { [key: string]: any },
) {
  const res = await request<
    API.BaseResponse<API.DictionaryTagLexiconOptionResponse>
  >('/api/admin/dictionaries/tag-lexicons', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
  if (res.code === 0) clearTagLexiconCache();
  return res;
}

/** PUT /api/admin/dictionaries/tag-lexicons/{lexicon_id} */
export async function updateTagLexicon(
  lexiconId: string,
  body: API.DictionaryTagLexiconUpdateRequest,
  options?: { [key: string]: any },
) {
  const res = await request<
    API.BaseResponse<API.DictionaryTagLexiconOptionResponse>
  >(`/api/admin/dictionaries/tag-lexicons/${lexiconId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
  if (res.code === 0) clearTagLexiconCache();
  return res;
}

/** DELETE /api/admin/dictionaries/tag-lexicons/{lexicon_id} */
export async function deleteTagLexicon(
  lexiconId: string,
  options?: { [key: string]: any },
) {
  const res = await request<API.BaseResponse<Record<string, string>>>(
    `/api/admin/dictionaries/tag-lexicons/${lexiconId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
  if (res.code === 0) clearTagLexiconCache();
  return res;
}
